import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { parseSite } from '@/lib/site';

import { apiRequest, apiRequestEnvelope } from './client';
import { queryKeys } from './keys';
import { useSession } from './auth';

export function useSite() {
  return useQuery({
    queryKey: queryKeys.site,
    queryFn: ({ signal }) => apiRequest('/site', { signal }),
    select: parseSite,
    staleTime: 5 * 60_000,
  });
}

export function useTrips(region?: string) {
  const session = useSession();
  const userId = session.data?.user.id ?? null;
  return useInfiniteQuery({
    queryKey: queryKeys.trips(region, userId),
    queryFn: ({ signal, pageParam }) =>
      apiRequestEnvelope('/trips', {
        signal,
        query: { region, limit: 24, cursor: pageParam ?? undefined },
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.page.nextCursor,
    enabled: !session.isLoading,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}

/** Public trip lookup accepts the canonical slug used by member-facing links. */
export function fetchTrip(slugOrId: string, signal?: AbortSignal) {
  return apiRequest(`/trips/${encodeURIComponent(slugOrId)}`, { signal });
}

export function useTrip(slug: string | undefined) {
  const session = useSession();
  const userId = session.data?.user.id ?? null;
  return useQuery({
    queryKey: queryKeys.trip(slug ?? '', userId),
    queryFn: ({ signal }) => fetchTrip(slug!, signal),
    enabled: Boolean(slug) && !session.isLoading,
    staleTime: 15_000,
    refetchOnWindowFocus: true,
  });
}

export function usePastTrips() {
  return useQuery({
    queryKey: queryKeys.pastTrips,
    queryFn: ({ signal }) => apiRequest('/past-trips', { signal }),
    staleTime: 5 * 60_000,
  });
}

export function useGallery() {
  return useQuery({
    queryKey: queryKeys.gallery,
    queryFn: ({ signal }) => apiRequest('/gallery', { signal }),
    staleTime: 5 * 60_000,
  });
}

export function usePolicies() {
  return useQuery({
    queryKey: queryKeys.policies,
    queryFn: ({ signal }) => apiRequest('/policies/current', { signal }),
    staleTime: 60_000,
  });
}
